import { getProductWeight, saleSatisfiesWeightCondition } from 'shared/helpers/products';
import { Product } from 'utils/helpers/product';

import { MenuType } from './use-product-pricing.types';

export type SpecialRestriction = {
  appliesToAnyWeight: boolean;
  discount?: number;
  discountType?: string;
  isPercentDiscount: boolean;
  weight?: number;
  weights?: number[];
  weightOperator?: 'equalTo' | 'greaterThan' | 'greaterThanEqualTo';
};

export function getIsSpecialApplicable(weight: string, specialRestrictions: SpecialRestriction[]): boolean {
  const weightValue = getProductWeight(weight);
  return specialRestrictions.some((special) =>
    saleSatisfiesWeightCondition(
      weightValue,
      special.weightOperator,
      special.weights ?? special.weight,
      special.appliesToAnyWeight
    )
  );
}

export type TransformSpecialRestrictionsProps = {
  menuType: MenuType;
  productSpecialsKeys: Set<string>;
  specialData: Product['specialData'];
};

export function transformSpecialRestrictions({
  menuType,
  productSpecialsKeys,
  specialData,
}: TransformSpecialRestrictionsProps): SpecialRestriction[] {
  const restrictions: SpecialRestriction[] = [];

  if (!specialData?.saleSpecials || !Array.isArray(specialData.saleSpecials)) {
    return restrictions;
  }

  specialData.saleSpecials.forEach((special) => {
    if (special?.specialRestrictions) {
      const specialRestrictions = special.specialRestrictions ?? {};
      const discount = special.discount ?? undefined;
      const isPercentDiscount = special.percentDiscount ?? false;

      Object.entries(specialRestrictions).forEach((item: [string, any]) => {
        const [key, { weight, weights, weightOperator, discountType }] = item;

        const parsedWeights =
          weights?.length > 0
            ? weights?.map((currentWeight) => getProductWeight(currentWeight))
            : [getProductWeight(weight)];

        if (productSpecialsKeys.has(key) && (special.menuType === 'both' || special.menuType === menuType)) {
          restrictions.push({
            appliesToAnyWeight: !weight && !weights && !weightOperator,
            discount,
            discountType,
            isPercentDiscount,
            weight: weight && getProductWeight(weight),
            weights: parsedWeights,
            weightOperator,
          });
        }
      });
    }
  });

  return restrictions;
}

export function getProductSpecialsKeys({
  brandId,
  brand,
  brandName,
  type,
  subcategory,
  _id,
  id,
}: Product): Set<string> {
  const productSpecialsKeys = [brandId ?? brand.id, brandName ?? brand.name, type, subcategory, _id ?? id];
  const productSpecialsKeysSet = new Set<string>();

  productSpecialsKeys.forEach((key) => {
    if (typeof key === 'string') {
      productSpecialsKeysSet.add(key);
    }
  });

  return productSpecialsKeysSet;
}

export const calcDiscountPercentage = (specialPrice: number, price: number): number =>
  Math.round((1 - specialPrice / price) * 100);
